import { ShapeUtils, Vector2, Vector3 } from "three";

type PlaneBasis = { origin: Vector3; u: Vector3; v: Vector3 };

/** Above this, the normal is too close to the vertical to build the basis from the up axis */
const MAX_NORMAL_UP_ALIGNMENT = 0.9;

/**
 * @param points corners of the polygon
 * @returns an orthonormal basis of the best fitting plane of the points (Newell's method)
 */
function planeBasis(points: Vector3[]): PlaneBasis {
  const normal = new Vector3();
  const origin = new Vector3();
  points.forEach((p, i) => {
    const q = points[(i + 1) % points.length];
    normal.x += (p.y - q.y) * (p.z + q.z);
    normal.y += (p.z - q.z) * (p.x + q.x);
    normal.z += (p.x - q.x) * (p.y + q.y);
    origin.add(p);
  });
  normal.normalize();
  origin.divideScalar(points.length);
  const helper =
    Math.abs(normal.y) < MAX_NORMAL_UP_ALIGNMENT
      ? new Vector3(0, 1, 0)
      : new Vector3(1, 0, 0);
  const u = helper.cross(normal).normalize();
  const v = normal.clone().cross(u);
  return { origin, u, v };
}

function to2D(points: Vector3[], { origin, u, v }: PlaneBasis): Vector2[] {
  return points.map((p) => {
    const d = p.clone().sub(origin);
    return new Vector2(d.dot(u), d.dot(v));
  });
}

function to3D(p: Vector2, { origin, u, v }: PlaneBasis): Vector3 {
  return origin.clone().addScaledVector(u, p.x).addScaledVector(v, p.y);
}

/**
 * @param points corners of the polygon
 * @returns the length of the closed outline through the points
 */
export function perimeter(points: Vector3[]): number {
  return points.reduce(
    (sum, p, i) => sum + p.distanceTo(points[(i + 1) % points.length]),
    0,
  );
}

/**
 * @returns the points of a revision cloud around the closed polygon: each edge becomes a chain of outward arcs
 * @param points corners of the polygon
 * @param arcLength target length of the chord of each arc
 * @param segmentsPerArc number of line segments used to draw each arc
 */
export function cloudOutline(
  points: Vector3[],
  arcLength: number,
  segmentsPerArc = 8,
): Vector3[] {
  const basis = planeBasis(points);
  const corners = to2D(points, basis);
  // Outward is to the right of the edge direction for counter-clockwise polygons
  const side = ShapeUtils.isClockWise(corners) ? -1 : 1;

  const outline: Vector2[] = [corners[0].clone()];
  corners.forEach((a, i) => {
    const b = corners[(i + 1) % corners.length];
    const arcs = Math.max(1, Math.round(a.distanceTo(b) / arcLength));
    const step = b.clone().sub(a).divideScalar(arcs);
    const dir = step.clone().normalize();
    const outward = new Vector2(dir.y, -dir.x).multiplyScalar(side);
    const radius = step.length() / 2;
    for (let k = 0; k < arcs; k++) {
      const center = a.clone().addScaledVector(step, k + 0.5);
      for (let s = 1; s <= segmentsPerArc; s++) {
        const angle = (Math.PI * s) / segmentsPerArc;
        outline.push(
          center
            .clone()
            .addScaledVector(dir, -radius * Math.cos(angle))
            .addScaledVector(outward, radius * Math.sin(angle)),
        );
      }
    }
  });
  return outline.map((p) => to3D(p, basis));
}
